/**
 * Component Library Demo Page
 *
 * Purpose: Showcase all components (MUI and custom) used in the application for internal
 *          experimentation and testing of different props and states.
 *
 * Architecture: Admin page that displays all reusable components with
 *              interactive controls for testing different configurations.
 *
 * Dependencies: React, Material-UI, @mui/icons-material, custom components
 * Author: Development Team
 * Created: 2025-01-27
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  ConfirmDialog,
  DestructiveButton,
  DialogFooter,
  RoleSelect,
  RowActionButton,
  RowActions,
  SecondaryButton,
} from '../../components/common';
import { authzService } from '../../services/authzService';
import type { Role } from '../../types/authz';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Switch,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Avatar,
  Container,
  Checkbox,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Visibility,
  VisibilityOff,
  DashboardOutlined,
  PersonOutlined,
  AdminPanelSettingsOutlined,
  PaletteOutlined,
  Check,
  Close,
  Home as HomeIcon,
  AdminPanelSettings as AdminIcon,
  Restore as RestoreIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { MDTable, TableColumn } from '../../components/md-table';
import {
  generateCampaignData,
  generateMediaPlanData,
  generateVendorData,
  formatCurrency,
  formatNumber,
  formatPercent,
  formatDate,
} from '../../utils/tableData';
import { MetadataForm } from '../../components/metadata-form/MetadataForm';
import { metadataService } from '../../services/metadataService';
import type { ViewConfiguration, ViewField } from '../../components/metadata-form/types';
import type { FieldDefinition } from '../../services/metadataService';
import SkeletonPage from '../../components/layout/SkeletonPage';

// Styled components for demo layout
const DemoContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: '100%',
  overflow: 'auto',
}));

const ComponentCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
}));

const ControlPanel = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`component-tabpanel-${index}`}
      aria-labelledby={`component-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * Component Library Demo Page Component
 *
 * Purpose: Provides interactive demo environment for all components (MUI and custom)
 * Logic: Renders components with configurable props and state controls
 *
 * @returns JSX element containing the demo interface
 */
const ComponentLibraryDemo: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  // RoleSelect and ConfirmDialog demos (ORCH-336): the real role catalog, no sample data
  const [demoRoles, setDemoRoles] = useState<Role[]>([]);
  const [demoRoleIds, setDemoRoleIds] = useState<string[]>([]);
  const [demoConfirmOpen, setDemoConfirmOpen] = useState(false);
  // RowActions demo (ORCH-298)
  const [demoRowActive, setDemoRowActive] = useState(true);
  const [demoUserActive, setDemoUserActive] = useState(true);
  useEffect(() => {
    authzService
      .listRoles()
      .then(setDemoRoles)
      .catch(() => setDemoRoles([]));
  }, []);

  // Metadata Form state
  const [_fieldDefinitions, setFieldDefinitions] = useState<FieldDefinition[]>([]);
  const [metadataFormLoading, setMetadataFormLoading] = useState(false);
  const [metadataFormError, setMetadataFormError] = useState<string | null>(null);
  const [mockViewConfig, setMockViewConfig] = useState<ViewConfiguration | null>(null);

  const [demoState, setDemoState] = useState({
    // Button demo state
    buttonVariant: 'contained',
    buttonColor: 'primary',
    buttonSize: 'medium',
    buttonDisabled: false,

    // TextField demo state
    textFieldValue: 'Sample text',
    textFieldType: 'text',
    textFieldError: false,
    textFieldHelperText: '',
    textFieldShowPassword: false,

    // Alert demo state
    alertSeverity: 'info',
    alertMessage: 'This is a sample alert message',
    alertShowIcon: true,
    alertShowAction: false,

    // Chip demo state
    chipColor: 'primary',
    chipVariant: 'filled',
    chipLabel: 'Sample Chip',
    chipDeletable: false,

    // Switch demo state
    switchChecked: false,
    switchDisabled: false,

    // Select demo state
    selectValue: 'option1',
    selectDisabled: false,

    // Avatar demo state
    avatarSize: 'medium',
    avatarColor: 'primary',

    // Table demo state
    tableDataset: 'campaigns',
    tableSelectable: true,
    tableFilterable: true,
    tableSortable: true,
    tableEditMode: false,
    hideGlobal: false,
    modelFilter: '',
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleDemoStateChange = (key: string, value: any) => {
    setDemoState((prev: any) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Breadcrumbs for the page
  const breadcrumbs = [
    { label: 'Home', href: '/home', icon: <HomeIcon fontSize="inherit" /> },
    { label: 'Admin', href: '/admin', icon: <AdminIcon fontSize="inherit" /> },
    { label: 'Component Library', icon: <PaletteOutlined fontSize="inherit" /> },
  ];

  // Fetch field definitions and build mock view configuration
  useEffect(() => {
    const fetchFieldDefinitionsAndBuildConfig = async () => {
      setMetadataFormLoading(true);
      setMetadataFormError(null);

      try {
        // Fetch all field definitions from GLOBAL tenant
        const allFieldDefs = await metadataService.getFieldDefinitions({
          tenant_id: 'GLOBAL',
        });

        // Find the 8 specific field definitions we need
        const fieldDefIds = [
          'fd_media-agency',
          'fd_product',
          'fd_country',
          'fd_start-date',
          'fd_end-date',
          'fd_budget',
          'fd_media-cost',
          'fd_campaign-name',
          'fd_daypart',
        ];

        const foundFieldDefs = fieldDefIds
          .map((id) => allFieldDefs.find((fd) => fd.fielddef_id === id))
          .filter((fd): fd is FieldDefinition => fd !== undefined);

        setFieldDefinitions(foundFieldDefs);

        // Create mock field definition for Sample Boolean
        const mockBooleanFieldDef: FieldDefinition = {
          fielddef_id: 'fd_sample-boolean',
          tenant_id: 'GLOBAL',
          label: 'Sample Boolean',
          description: null,
          datatype: 'bool',
          is_multiselect: false,
          creation_access: 'None',
          storage_decimal_places: null,
          display_decimal_places: null,
          status: 'active',
          access: '2',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          created_by: 'system',
          updated_by: 'system',
        };

        // Build ViewField array in the correct order
        const viewFields: ViewField[] = [];
        const tenantId = 'GLOBAL';
        const modelId = 'sample-model';

        // 1. Media Agency - single select
        const mediaAgencyDef = foundFieldDefs.find((fd) => fd.fielddef_id === 'fd_media-agency');
        if (mediaAgencyDef) {
          viewFields.push({
            field_id: 'field_1',
            fielddef_id: 'fd_media-agency',
            tenant_id: tenantId,
            model_id: modelId,
            storage_type: 'metadata',
            label_override: undefined,
            is_required: false,
            view_position: 1,
            display_order: 1,
            storage_decimal_places: mediaAgencyDef.storage_decimal_places ?? undefined,
            display_decimal_places: mediaAgencyDef.display_decimal_places ?? undefined,
            field_definition: {
              fielddef_id: mediaAgencyDef.fielddef_id,
              label: mediaAgencyDef.label,
              datatype: mediaAgencyDef.datatype,
              is_multiselect: mediaAgencyDef.is_multiselect,
              storage_decimal_places: mediaAgencyDef.storage_decimal_places,
              display_decimal_places: mediaAgencyDef.display_decimal_places,
              hierarchy_config: mediaAgencyDef.hierarchy_config ?? null,
            },
          });
        }

        // 2. Product (Business) - multiselect
        const productDef = foundFieldDefs.find((fd) => fd.fielddef_id === 'fd_product');
        if (productDef) {
          viewFields.push({
            field_id: 'field_2',
            fielddef_id: 'fd_product',
            tenant_id: tenantId,
            model_id: modelId,
            storage_type: 'metadata',
            label_override: 'Business',
            is_required: false,
            view_position: 2,
            display_order: 2,
            storage_decimal_places: productDef.storage_decimal_places ?? undefined,
            display_decimal_places: productDef.display_decimal_places ?? undefined,
            field_definition: {
              fielddef_id: productDef.fielddef_id,
              label: productDef.label,
              datatype: productDef.datatype,
              is_multiselect: productDef.is_multiselect,
              storage_decimal_places: productDef.storage_decimal_places,
              display_decimal_places: productDef.display_decimal_places,
              hierarchy_config: productDef.hierarchy_config ?? null,
            },
          });
        }

        // 3. Country (Geography) - single select
        const countryDef = foundFieldDefs.find((fd) => fd.fielddef_id === 'fd_country');
        if (countryDef) {
          viewFields.push({
            field_id: 'field_3',
            fielddef_id: 'fd_country',
            tenant_id: tenantId,
            model_id: modelId,
            storage_type: 'metadata',
            label_override: 'Geography',
            is_required: false,
            view_position: 3,
            display_order: 3,
            storage_decimal_places: countryDef.storage_decimal_places ?? undefined,
            display_decimal_places: countryDef.display_decimal_places ?? undefined,
            field_definition: {
              fielddef_id: countryDef.fielddef_id,
              label: countryDef.label,
              datatype: countryDef.datatype,
              is_multiselect: false, // Override: single select for Geography
              storage_decimal_places: countryDef.storage_decimal_places,
              display_decimal_places: countryDef.display_decimal_places,
              hierarchy_config: countryDef.hierarchy_config ?? null,
            },
          });
        }

        // 4. Start Date
        const startDateDef = foundFieldDefs.find((fd) => fd.fielddef_id === 'fd_start-date');
        if (startDateDef) {
          viewFields.push({
            field_id: 'field_4',
            fielddef_id: 'fd_start-date',
            tenant_id: tenantId,
            model_id: modelId,
            storage_type: 'metadata',
            label_override: undefined,
            is_required: false,
            view_position: 4,
            display_order: 4,
            storage_decimal_places: startDateDef.storage_decimal_places ?? undefined,
            display_decimal_places: startDateDef.display_decimal_places ?? undefined,
            field_definition: {
              fielddef_id: startDateDef.fielddef_id,
              label: startDateDef.label,
              datatype: startDateDef.datatype,
              is_multiselect: startDateDef.is_multiselect,
              storage_decimal_places: startDateDef.storage_decimal_places,
              display_decimal_places: startDateDef.display_decimal_places,
              hierarchy_config: startDateDef.hierarchy_config ?? null,
            },
          });
        }

        // 5. End Date
        const endDateDef = foundFieldDefs.find((fd) => fd.fielddef_id === 'fd_end-date');
        if (endDateDef) {
          viewFields.push({
            field_id: 'field_5',
            fielddef_id: 'fd_end-date',
            tenant_id: tenantId,
            model_id: modelId,
            storage_type: 'metadata',
            label_override: undefined,
            is_required: false,
            view_position: 5,
            display_order: 5,
            storage_decimal_places: endDateDef.storage_decimal_places ?? undefined,
            display_decimal_places: endDateDef.display_decimal_places ?? undefined,
            field_definition: {
              fielddef_id: endDateDef.fielddef_id,
              label: endDateDef.label,
              datatype: endDateDef.datatype,
              is_multiselect: endDateDef.is_multiselect,
              storage_decimal_places: endDateDef.storage_decimal_places,
              display_decimal_places: endDateDef.display_decimal_places,
              hierarchy_config: endDateDef.hierarchy_config ?? null,
            },
          });
        }

        // 6. Budget
        const budgetDef = foundFieldDefs.find((fd) => fd.fielddef_id === 'fd_budget');
        if (budgetDef) {
          viewFields.push({
            field_id: 'field_6',
            fielddef_id: 'fd_budget',
            tenant_id: tenantId,
            model_id: modelId,
            storage_type: 'metadata',
            label_override: undefined,
            is_required: false,
            view_position: 6,
            display_order: 6,
            storage_decimal_places: budgetDef.storage_decimal_places ?? undefined,
            display_decimal_places: budgetDef.display_decimal_places ?? undefined,
            field_definition: {
              fielddef_id: budgetDef.fielddef_id,
              label: budgetDef.label,
              datatype: budgetDef.datatype,
              is_multiselect: budgetDef.is_multiselect,
              storage_decimal_places: budgetDef.storage_decimal_places,
              display_decimal_places: budgetDef.display_decimal_places,
              hierarchy_config: budgetDef.hierarchy_config ?? null,
            },
          });
        }

        // 7. Media Cost
        const mediaCostDef = foundFieldDefs.find((fd) => fd.fielddef_id === 'fd_media-cost');
        if (mediaCostDef) {
          viewFields.push({
            field_id: 'field_7',
            fielddef_id: 'fd_media-cost',
            tenant_id: tenantId,
            model_id: modelId,
            storage_type: 'metadata',
            label_override: undefined,
            is_required: false,
            view_position: 7,
            display_order: 7,
            storage_decimal_places: mediaCostDef.storage_decimal_places ?? undefined,
            display_decimal_places: mediaCostDef.display_decimal_places ?? undefined,
            field_definition: {
              fielddef_id: mediaCostDef.fielddef_id,
              label: mediaCostDef.label,
              datatype: mediaCostDef.datatype,
              is_multiselect: mediaCostDef.is_multiselect,
              storage_decimal_places: mediaCostDef.storage_decimal_places,
              display_decimal_places: mediaCostDef.display_decimal_places,
              hierarchy_config: mediaCostDef.hierarchy_config ?? null,
            },
          });
        }

        // 8. Campaign Name
        const campaignNameDef = foundFieldDefs.find((fd) => fd.fielddef_id === 'fd_campaign-name');
        if (campaignNameDef) {
          viewFields.push({
            field_id: 'field_8',
            fielddef_id: 'fd_campaign-name',
            tenant_id: tenantId,
            model_id: modelId,
            storage_type: 'metadata',
            label_override: undefined,
            is_required: false,
            view_position: 8,
            display_order: 8,
            storage_decimal_places: campaignNameDef.storage_decimal_places ?? undefined,
            display_decimal_places: campaignNameDef.display_decimal_places ?? undefined,
            field_definition: {
              fielddef_id: campaignNameDef.fielddef_id,
              label: campaignNameDef.label,
              datatype: campaignNameDef.datatype,
              is_multiselect: campaignNameDef.is_multiselect,
              storage_decimal_places: campaignNameDef.storage_decimal_places,
              display_decimal_places: campaignNameDef.display_decimal_places,
              hierarchy_config: campaignNameDef.hierarchy_config ?? null,
            },
          });
        }

        // 9. Daypart - multiselect
        const daypartDef = foundFieldDefs.find((fd) => fd.fielddef_id === 'fd_daypart');
        if (daypartDef) {
          viewFields.push({
            field_id: 'field_9',
            fielddef_id: 'fd_daypart',
            tenant_id: tenantId,
            model_id: modelId,
            storage_type: 'metadata',
            label_override: undefined,
            is_required: false,
            view_position: 9,
            display_order: 9,
            storage_decimal_places: daypartDef.storage_decimal_places ?? undefined,
            display_decimal_places: daypartDef.display_decimal_places ?? undefined,
            field_definition: {
              fielddef_id: daypartDef.fielddef_id,
              label: daypartDef.label,
              datatype: daypartDef.datatype,
              is_multiselect: true, // Override: multiselect for Daypart
              storage_decimal_places: daypartDef.storage_decimal_places,
              display_decimal_places: daypartDef.display_decimal_places,
              hierarchy_config: daypartDef.hierarchy_config ?? null,
            },
          });
        }

        // 10. Sample Boolean (mock field)
        viewFields.push({
          field_id: 'field_10',
          fielddef_id: 'fd_sample-boolean',
          tenant_id: tenantId,
          model_id: modelId,
          storage_type: 'metadata',
          label_override: undefined,
          is_required: false,
          view_position: 10,
          display_order: 10,
          storage_decimal_places: undefined,
          display_decimal_places: undefined,
          field_definition: {
            fielddef_id: mockBooleanFieldDef.fielddef_id,
            label: mockBooleanFieldDef.label,
            datatype: mockBooleanFieldDef.datatype,
            is_multiselect: mockBooleanFieldDef.is_multiselect,
            storage_decimal_places: mockBooleanFieldDef.storage_decimal_places ?? null,
            display_decimal_places: mockBooleanFieldDef.display_decimal_places ?? null,
            hierarchy_config: null,
          },
        });

        // Build the view configuration
        const viewConfig: ViewConfiguration = {
          model_id: modelId,
          view_type: 'edit-modal',
          tenant_id: tenantId,
          fields: viewFields,
        };

        setMockViewConfig(viewConfig);
      } catch (err) {
        const errorMessage = (err as Error)?.message || 'Failed to load field definitions';
        setMetadataFormError(errorMessage);
      } finally {
        setMetadataFormLoading(false);
      }
    };

    // Fetch field definitions on mount
    fetchFieldDefinitionsAndBuildConfig();
  }, []);

  // Generate table data and columns based on selected dataset
  const tableData = useMemo(() => {
    switch (demoState.tableDataset) {
      case 'campaigns':
        return generateCampaignData(25);
      case 'mediaPlans':
        return generateMediaPlanData(15);
      case 'vendors':
        return generateVendorData(20);
      default:
        return generateCampaignData(25);
    }
  }, [demoState.tableDataset]) as any[];

  const tableColumns = useMemo((): TableColumn[] => {
    switch (demoState.tableDataset) {
      case 'campaigns':
        return [
          { field: 'id', headerName: 'ID', width: 90 },
          { field: 'name', headerName: 'Campaign Name', width: 200, flex: 1 },
          { field: 'brand', headerName: 'Brand', width: 120 },
          {
            field: 'status',
            headerName: 'Status',
            width: 100,
            type: 'singleSelect',
            valueOptions: [
              { value: 'active', label: 'Active' },
              { value: 'paused', label: 'Paused' },
              { value: 'completed', label: 'Completed' },
              { value: 'draft', label: 'Draft' },
            ],
            renderCell: (value: any) => (
              <Chip
                label={value}
                size="small"
                color={
                  value === 'active'
                    ? 'success'
                    : value === 'paused'
                      ? 'warning'
                      : value === 'completed'
                        ? 'info'
                        : 'default'
                }
              />
            ),
          },
          {
            field: 'budget',
            headerName: 'Budget',
            width: 110,
            renderCell: (value: any) => formatCurrency(value),
          },
          {
            field: 'spend',
            headerName: 'Spend',
            width: 110,
            renderCell: (value: any) => formatCurrency(value),
          },
          {
            field: 'impressions',
            headerName: 'Impressions',
            width: 120,
            renderCell: (value: any) => formatNumber(value),
          },
          {
            field: 'clicks',
            headerName: 'Clicks',
            width: 100,
            renderCell: (value: any) => formatNumber(value),
          },
          {
            field: 'ctr',
            headerName: 'CTR',
            width: 80,
            renderCell: (value: any) => formatPercent(value),
          },
          { field: 'platform', headerName: 'Platform', width: 120 },
          {
            field: 'startDate',
            headerName: 'Start Date',
            width: 120,
            renderCell: (value: any) => formatDate(value),
          },
        ];

      case 'mediaPlans':
        return [
          { field: 'id', headerName: 'ID', width: 90 },
          { field: 'name', headerName: 'Plan Name', width: 250, flex: 1 },
          { field: 'client', headerName: 'Client', width: 140 },
          {
            field: 'status',
            headerName: 'Status',
            width: 100,
            type: 'singleSelect',
            valueOptions: [
              { value: 'approved', label: 'Approved' },
              { value: 'in-flight', label: 'In Flight' },
              { value: 'completed', label: 'Completed' },
              { value: 'draft', label: 'Draft' },
            ],
            renderCell: (value: any) => (
              <Chip
                label={value}
                size="small"
                color={
                  value === 'approved'
                    ? 'success'
                    : value === 'in-flight'
                      ? 'info'
                      : value === 'completed'
                        ? 'default'
                        : 'warning'
                }
              />
            ),
          },
          {
            field: 'totalBudget',
            headerName: 'Budget',
            width: 120,
            renderCell: (value: any) => formatCurrency(value),
          },
          { field: 'flightDates', headerName: 'Flight Dates', width: 140 },
          {
            field: 'channels',
            headerName: 'Channels',
            width: 150,
            type: 'multipleSelect',
            valueOptions: [
              { value: 'tv', label: 'TV' },
              { value: 'radio', label: 'Radio' },
              { value: 'digital', label: 'Digital' },
              { value: 'print', label: 'Print' },
              { value: 'outdoor', label: 'Outdoor' },
              { value: 'social', label: 'Social Media' },
            ],
            renderCell: (value: any) => {
              if (Array.isArray(value)) {
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {value.map((channel: string) => (
                      <Chip key={channel} label={channel} size="small" variant="outlined" />
                    ))}
                  </Box>
                );
              }
              return value;
            },
          },
          { field: 'targetDemo', headerName: 'Target Demo', width: 140 },
          { field: 'createdBy', headerName: 'Created By', width: 120 },
        ];

      case 'vendors':
        return [
          { field: 'id', headerName: 'ID', width: 90 },
          { field: 'name', headerName: 'Vendor Name', width: 200, flex: 1 },
          {
            field: 'type',
            headerName: 'Type',
            width: 100,
            type: 'singleSelect',
            valueOptions: [
              { value: 'agency', label: 'Agency' },
              { value: 'publisher', label: 'Publisher' },
              { value: 'platform', label: 'Platform' },
              { value: 'vendor', label: 'Vendor' },
            ],
            renderCell: (value: any) => <Chip label={value} size="small" variant="outlined" />,
          },
          {
            field: 'status',
            headerName: 'Status',
            width: 100,
            renderCell: (value: any) => (
              <Chip
                label={value}
                size="small"
                color={value === 'active' ? 'success' : value === 'inactive' ? 'error' : 'warning'}
              />
            ),
          },
          {
            field: 'rating',
            headerName: 'Rating',
            width: 80,
            renderCell: (value: any) => `${value}/5.0`,
          },
          {
            field: 'totalSpend',
            headerName: 'Total Spend',
            width: 120,
            renderCell: (value: any) => formatCurrency(value),
          },
          {
            field: 'campaigns',
            headerName: 'Campaigns',
            width: 100,
            type: 'multipleSelect',
            valueOptions: [
              { value: 'brand_awareness', label: 'Brand Awareness' },
              { value: 'lead_generation', label: 'Lead Generation' },
              { value: 'conversion', label: 'Conversion' },
              { value: 'retargeting', label: 'Retargeting' },
              { value: 'product_launch', label: 'Product Launch' },
            ],
            renderCell: (value: any) => {
              if (Array.isArray(value)) {
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {value.map((campaign: string) => (
                      <Chip
                        key={campaign}
                        label={campaign.replace('_', ' ')}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                );
              }
              return formatNumber(value);
            },
          },
          { field: 'primaryContact', headerName: 'Contact', width: 140 },
          { field: 'paymentTerms', headerName: 'Payment Terms', width: 120 },
        ];

      default:
        return [];
    }
  }, [demoState.tableDataset]);

  const getTableTitle = () => {
    switch (demoState.tableDataset) {
      case 'campaigns':
        return 'Campaign Performance';
      case 'mediaPlans':
        return 'Media Plans Overview';
      case 'vendors':
        return 'Vendor Directory';
      default:
        return 'Data Table';
    }
  };

  const getTableDescription = () => {
    switch (demoState.tableDataset) {
      case 'campaigns':
        return 'Track and analyze campaign performance across all platforms';
      case 'mediaPlans':
        return 'Manage media plans and track approval status';
      case 'vendors':
        return 'Directory of media vendors and their performance metrics';
      default:
        return 'Sample data table with various controls';
    }
  };

  return (
    <SkeletonPage pageTitle="Component Library" breadcrumbs={breadcrumbs}>
      <DemoContainer>
        <Typography variant="h4" gutterBottom>
          MUI Component Demo
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Interactive demonstration of all Material-UI components used in the OrchestrateHQ AI
          application.
        </Typography>

        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="component tabs">
            <Tab label="Buttons & Actions" />
            <Tab label="Form Controls" />
            <Tab label="Feedback & Status" />
            <Tab label="Data Display" />
            <Tab label="Table" />
            <Tab label="Custom Components" />
            <Tab label="Metadata Form" />
          </Tabs>
        </Paper>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Buttons & Actions
          </Typography>

          <ControlPanel>
            <Typography variant="subtitle2" gutterBottom>
              Button Controls
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Variant</InputLabel>
                  <Select
                    value={demoState.buttonVariant}
                    onChange={(e: any) => handleDemoStateChange('buttonVariant', e.target.value)}
                    label="Variant"
                  >
                    <MenuItem value="contained">Contained</MenuItem>
                    <MenuItem value="outlined">Outlined</MenuItem>
                    <MenuItem value="text">Text</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Color</InputLabel>
                  <Select
                    value={demoState.buttonColor}
                    onChange={(e: any) => handleDemoStateChange('buttonColor', e.target.value)}
                    label="Color"
                  >
                    <MenuItem value="primary">Primary</MenuItem>
                    <MenuItem value="secondary">Secondary</MenuItem>
                    <MenuItem value="success">Success</MenuItem>
                    <MenuItem value="error">Error</MenuItem>
                    <MenuItem value="warning">Warning</MenuItem>
                    <MenuItem value="info">Info</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Size</InputLabel>
                  <Select
                    value={demoState.buttonSize}
                    onChange={(e: any) => handleDemoStateChange('buttonSize', e.target.value)}
                    label="Size"
                  >
                    <MenuItem value="small">Small</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="large">Large</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={demoState.buttonDisabled}
                      onChange={(e: any) =>
                        handleDemoStateChange('buttonDisabled', e.target.checked)
                      }
                    />
                  }
                  label="Disabled"
                />
              </Grid>
            </Grid>
          </ControlPanel>

          <ComponentCard>
            <CardHeader title="Button Component" />
            <CardContent>
              <Box sx={{ border: '1px dashed #ccc', p: 2, borderRadius: 1 }}>
                <Button
                  variant={demoState.buttonVariant as any}
                  color={demoState.buttonColor as any}
                  size={demoState.buttonSize as any}
                  disabled={demoState.buttonDisabled}
                  startIcon={<DashboardOutlined />}
                >
                  Sample Button
                </Button>
              </Box>
            </CardContent>
          </ComponentCard>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Form Controls
          </Typography>

          <ControlPanel>
            <Typography variant="subtitle2" gutterBottom>
              TextField Controls
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="Value"
                  value={demoState.textFieldValue}
                  onChange={(e: any) => handleDemoStateChange('textFieldValue', e.target.value)}
                  margin="normal"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={demoState.textFieldType}
                    onChange={(e: any) => handleDemoStateChange('textFieldType', e.target.value)}
                    label="Type"
                  >
                    <MenuItem value="text">Text</MenuItem>
                    <MenuItem value="password">Password</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="number">Number</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={demoState.textFieldError}
                      onChange={(e: any) =>
                        handleDemoStateChange('textFieldError', e.target.checked)
                      }
                    />
                  }
                  label="Error State"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={demoState.textFieldShowPassword}
                      onChange={(e: any) =>
                        handleDemoStateChange('textFieldShowPassword', e.target.checked)
                      }
                    />
                  }
                  label="Show Password Toggle"
                />
              </Grid>
            </Grid>
          </ControlPanel>

          <ComponentCard>
            <CardHeader title="TextField Component" />
            <CardContent>
              <Box sx={{ border: '1px dashed #ccc', p: 2, borderRadius: 1 }}>
                <TextField
                  fullWidth
                  label="Sample TextField"
                  value={demoState.textFieldValue}
                  type={demoState.textFieldType}
                  error={demoState.textFieldError}
                  helperText={
                    demoState.textFieldError
                      ? 'This field has an error'
                      : demoState.textFieldHelperText
                  }
                  InputProps={{
                    endAdornment:
                      demoState.textFieldShowPassword && demoState.textFieldType === 'password' ? (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              handleDemoStateChange(
                                'textFieldShowPassword',
                                !demoState.textFieldShowPassword
                              )
                            }
                            edge="end"
                          >
                            {demoState.textFieldShowPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                  }}
                />
              </Box>
            </CardContent>
          </ComponentCard>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Feedback & Status
          </Typography>

          <ControlPanel>
            <Typography variant="subtitle2" gutterBottom>
              Alert Controls
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Severity</InputLabel>
                  <Select
                    value={demoState.alertSeverity}
                    onChange={(e: any) => handleDemoStateChange('alertSeverity', e.target.value)}
                    label="Severity"
                  >
                    <MenuItem value="error">Error</MenuItem>
                    <MenuItem value="warning">Warning</MenuItem>
                    <MenuItem value="info">Info</MenuItem>
                    <MenuItem value="success">Success</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Message"
                  value={demoState.alertMessage}
                  onChange={(e: any) => handleDemoStateChange('alertMessage', e.target.value)}
                  margin="normal"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={demoState.alertShowIcon}
                      onChange={(e: any) =>
                        handleDemoStateChange('alertShowIcon', e.target.checked)
                      }
                    />
                  }
                  label="Show Icon"
                />
              </Grid>
            </Grid>
          </ControlPanel>

          <ComponentCard>
            <CardHeader title="Alert Component" />
            <CardContent>
              <Box sx={{ border: '1px dashed #ccc', p: 2, borderRadius: 1 }}>
                <Alert
                  severity={demoState.alertSeverity as any}
                  icon={demoState.alertShowIcon ? undefined : false}
                  action={
                    demoState.alertShowAction ? (
                      <IconButton color="inherit" size="small" onClick={() => {}}>
                        <Close fontSize="inherit" />
                      </IconButton>
                    ) : undefined
                  }
                >
                  {demoState.alertMessage}
                </Alert>
              </Box>
            </CardContent>
          </ComponentCard>

          <ComponentCard>
            <CardHeader title="CircularProgress Component" />
            <CardContent>
              <Box sx={{ border: '1px dashed #ccc', p: 2, borderRadius: 1 }}>
                <Box display="flex" gap={2} alignItems="center">
                  <CircularProgress size={20} />
                  <CircularProgress size={30} />
                  <CircularProgress size={40} />
                  <CircularProgress size={50} />
                </Box>
              </Box>
            </CardContent>
          </ComponentCard>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom>
            Data Display
          </Typography>

          <ControlPanel>
            <Typography variant="subtitle2" gutterBottom>
              Chip Controls
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="Label"
                  value={demoState.chipLabel}
                  onChange={(e: any) => handleDemoStateChange('chipLabel', e.target.value)}
                  margin="normal"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Color</InputLabel>
                  <Select
                    value={demoState.chipColor}
                    onChange={(e: any) => handleDemoStateChange('chipColor', e.target.value)}
                    label="Color"
                  >
                    <MenuItem value="default">Default</MenuItem>
                    <MenuItem value="primary">Primary</MenuItem>
                    <MenuItem value="secondary">Secondary</MenuItem>
                    <MenuItem value="success">Success</MenuItem>
                    <MenuItem value="error">Error</MenuItem>
                    <MenuItem value="warning">Warning</MenuItem>
                    <MenuItem value="info">Info</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Variant</InputLabel>
                  <Select
                    value={demoState.chipVariant}
                    onChange={(e: any) => handleDemoStateChange('chipVariant', e.target.value)}
                    label="Variant"
                  >
                    <MenuItem value="filled">Filled</MenuItem>
                    <MenuItem value="outlined">Outlined</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={demoState.chipDeletable}
                      onChange={(e: any) =>
                        handleDemoStateChange('chipDeletable', e.target.checked)
                      }
                    />
                  }
                  label="Deletable"
                />
              </Grid>
            </Grid>
          </ControlPanel>

          <ComponentCard>
            <CardHeader title="Chip Component" />
            <CardContent>
              <Box sx={{ border: '1px dashed #ccc', p: 2, borderRadius: 1 }}>
                <Chip
                  label={demoState.chipLabel}
                  color={demoState.chipColor as any}
                  variant={demoState.chipVariant as any}
                  onDelete={demoState.chipDeletable ? () => {} : undefined}
                  icon={<Check />}
                />
              </Box>
            </CardContent>
          </ComponentCard>

          <ComponentCard>
            <CardHeader title="Avatar Component" />
            <CardContent>
              <Box sx={{ border: '1px dashed #ccc', p: 2, borderRadius: 1 }}>
                <Box display="flex" gap={2} alignItems="center">
                  <Avatar sx={{ bgcolor: `${demoState.avatarColor}.main` }}>
                    <PersonOutlined />
                  </Avatar>
                  <Avatar sx={{ bgcolor: 'secondary.main' }}>
                    <AdminPanelSettingsOutlined />
                  </Avatar>
                  <Avatar sx={{ bgcolor: 'success.main' }}>
                    <DashboardOutlined />
                  </Avatar>
                </Box>
              </Box>
            </CardContent>
          </ComponentCard>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Typography variant="h6" gutterBottom>
            Table Components
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            This section showcases our custom table components, starting with the MDTable component.
            The MDTable supports both manual and metadata-driven configurations, with advanced
            features like sorting, filtering, pagination, and row selection.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>New Features:</strong> The MDTable now includes infinite scroll (no
              pagination), compact density by default, built-in toolbar with quick filtering and
              column visibility controls, metadata datatype mapping, and inline editing
              capabilities!
              <br />
              <br />
              <strong>Metadata Mapping:</strong> When using metadata mode, the component
              automatically maps:
              <br />• <code>datatype: "list"</code> + <code>is_multiselect: false</code> →
              SingleSelect dropdown
              <br />• <code>datatype: "list"</code> + <code>is_multiselect: true</code> →
              MultipleSelect dropdown
              <br />• <code>datatype: "text"</code> → Text input
              <br />• <code>datatype: "number"</code> → Number input
              <br />• <code>datatype: "date"</code> → Date picker
              <br />• <code>datatype: "boolean"</code> → Checkbox
              <br />
              <br />
              Try the controls below to explore all features.
            </Typography>
          </Alert>

          <ControlPanel>
            <Typography variant="subtitle2" gutterBottom>
              MDTable Controls
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Dataset</InputLabel>
                  <Select
                    value={demoState.tableDataset}
                    onChange={(e: any) => handleDemoStateChange('tableDataset', e.target.value)}
                    label="Dataset"
                  >
                    <MenuItem value="campaigns">Campaigns</MenuItem>
                    <MenuItem value="mediaPlans">Media Plans</MenuItem>
                    <MenuItem value="vendors">Vendors</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={demoState.tableSelectable}
                      onChange={(e: any) =>
                        handleDemoStateChange('tableSelectable', e.target.checked)
                      }
                    />
                  }
                  label="Row Selection"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={demoState.tableFilterable}
                      onChange={(e: any) =>
                        handleDemoStateChange('tableFilterable', e.target.checked)
                      }
                    />
                  }
                  label="Filterable"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={demoState.tableSortable}
                      onChange={(e: any) =>
                        handleDemoStateChange('tableSortable', e.target.checked)
                      }
                    />
                  }
                  label="Sortable"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={demoState.tableEditMode}
                      onChange={(e: any) =>
                        handleDemoStateChange('tableEditMode', e.target.checked)
                      }
                    />
                  }
                  label="Edit Mode"
                />
              </Grid>
            </Grid>
          </ControlPanel>

          <ComponentCard>
            <CardHeader title="MDTable Component (Manual Mode)" />
            <CardContent>
              <Box sx={{ border: '1px dashed #ccc', p: 2, borderRadius: 1 }}>
                <MDTable
                  mode="manual"
                  manual={{
                    columns: tableColumns,
                    title: getTableTitle(),
                    description: getTableDescription(),
                  }}
                  data={tableData}
                  selectable={demoState.tableSelectable}
                  filterable={demoState.tableFilterable}
                  sortable={demoState.tableSortable}
                  editMode={demoState.tableEditMode}
                  onRowClick={(_row) => {
                    // Row clicked event handled
                  }}
                  onSelectionChange={(_selectedIds) => {
                    // Selection changed event handled
                  }}
                  onExport={(_format) => {
                    // Export requested event handled
                  }}
                />
              </Box>
            </CardContent>
          </ComponentCard>

          <ComponentCard>
            <CardHeader title="MDTable with Custom Toolbar Filters" />
            <CardContent>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Custom Toolbar Filters Demo:</strong> This demonstrates the new custom
                  toolbar filters functionality. The MDTable now supports adding custom filter
                  controls directly in the toolbar for maximum space efficiency.
                  <br />
                  <br />
                  <strong>Features:</strong>
                  <br />• Generic filter support via <code>customToolbarFilters</code> prop
                  <br />• Compact design optimized for toolbar space
                  <br />• Supports checkboxes, dropdowns, and custom components
                  <br />• Positioned after built-in toolbar controls
                  <br />
                  <br />
                  Try the filter controls in the toolbar below!
                </Typography>
              </Alert>

              <Box sx={{ border: '1px dashed #ccc', p: 2, borderRadius: 1 }}>
                <MDTable
                  mode="manual"
                  manual={{
                    columns: tableColumns,
                    title: 'Table with Custom Filters',
                    description: 'Demonstrating custom toolbar filters functionality',
                  }}
                  data={tableData}
                  selectable={demoState.tableSelectable}
                  filterable={demoState.tableFilterable}
                  sortable={demoState.tableSortable}
                  editMode={demoState.tableEditMode}
                  customToolbarFilters={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {/* Hide GLOBAL Filter */}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={demoState.hideGlobal}
                            onChange={(e) => handleDemoStateChange('hideGlobal', e.target.checked)}
                          />
                        }
                        label="Hide GLOBAL"
                        sx={{
                          '& .MuiFormControlLabel-label': {
                            fontSize: '0.875rem',
                            color: 'text.secondary',
                          },
                        }}
                      />

                      {/* Model Filter */}
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={demoState.modelFilter}
                          onChange={(e) => handleDemoStateChange('modelFilter', e.target.value)}
                          displayEmpty
                          size="small"
                          sx={{
                            fontSize: '0.875rem',
                            '& .MuiSelect-select': {
                              py: 0.5,
                              px: 1,
                            },
                          }}
                        >
                          <MenuItem value="" sx={{ fontSize: '0.875rem' }}>
                            All Models
                          </MenuItem>
                          <MenuItem value="campaign" sx={{ fontSize: '0.875rem' }}>
                            Campaign Model
                          </MenuItem>
                          <MenuItem value="vendor" sx={{ fontSize: '0.875rem' }}>
                            Vendor Model
                          </MenuItem>
                          <MenuItem value="media" sx={{ fontSize: '0.875rem' }}>
                            Media Model
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  }
                  onRowClick={(_row) => {
                    // Row clicked event handled
                  }}
                  onSelectionChange={(_selectedIds) => {
                    // Selection changed event handled
                  }}
                  onExport={(_format) => {
                    // Export requested event handled
                  }}
                />
              </Box>

              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Filter State:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Hide GLOBAL: {demoState.hideGlobal ? 'Enabled' : 'Disabled'}
                  <br />
                  Model Filter: {demoState.modelFilter || 'All Models'}
                </Typography>
              </Box>
            </CardContent>
          </ComponentCard>

          <ComponentCard>
            <CardHeader title="Future Table Components" />
            <CardContent>
              <Box sx={{ border: '1px dashed #ccc', p: 2, borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Additional table components will be added here as they are developed:
                </Typography>
                <Typography variant="body2" color="text.secondary" component="ul">
                  <li>Advanced filtering components</li>
                  <li>Custom cell renderers</li>
                  <li>Bulk action components</li>
                  <li>Table view management</li>
                  <li>Export functionality components</li>
                </Typography>
              </Box>
            </CardContent>
          </ComponentCard>
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <Typography variant="h6" gutterBottom>
            Custom Components
          </Typography>

          <ComponentCard>
            <CardHeader
              title="RoleSelect"
              subheader="The role picker: catalog roles grouped Standard Roles / Custom Roles, A to Z. Roles you can't assign aren't offered; one already held shows read-only. Used by the User Manager's Access Rights, the invite dialog and the list's role filter."
            />
            <CardContent>
              <Box sx={{ border: '1px dashed #ccc', p: 2, borderRadius: 1, maxWidth: 520 }}>
                <RoleSelect
                  roles={demoRoles}
                  value={demoRoleIds}
                  onChange={setDemoRoleIds}
                  dataTestId="demo-role-select"
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1 }}
                >
                  {`<RoleSelect roles={roles} value={roleIds} onChange={setRoleIds} />`} · the roles
                  are this tenant's catalog (GET /authz/roles)
                </Typography>
              </Box>
            </CardContent>
          </ComponentCard>

          <ComponentCard>
            <CardHeader
              title="Button hierarchy and DialogFooter"
              subheader="Primary is filled. Cancel, Back and Close use SecondaryButton; Delete, Remove and Deactivate use DestructiveButton. Outlined is only for two equally valid choices. Every dialog renders its buttons through DialogFooter: the destructive action alone at the far left, Cancel and the primary together at the right (ORCH-424)."
            />
            <CardContent>
              <Box
                sx={{ border: '1px dashed #ccc', borderRadius: 1 }}
                data-testid="button-hierarchy-demo"
              >
                <DialogFooter
                  destructiveAction={
                    <DestructiveButton startIcon={<DeleteIcon />}>Delete</DestructiveButton>
                  }
                >
                  <SecondaryButton>Cancel</SecondaryButton>
                  <Button variant="contained">Update</Button>
                </DialogFooter>
              </Box>
            </CardContent>
          </ComponentCard>

          <ComponentCard>
            <CardHeader
              title="ConfirmDialog"
              subheader="A plain confirmation for reversible or routine actions (deactivate, remove from a tenant). Deletes that should make the person type the item's name use DeleteConfirmationDialog."
            />
            <CardContent>
              <Box sx={{ border: '1px dashed #ccc', p: 2, borderRadius: 1 }}>
                <DestructiveButton onClick={() => setDemoConfirmOpen(true)}>
                  Deactivate…
                </DestructiveButton>
                <ConfirmDialog
                  open={demoConfirmOpen}
                  title="Deactivate Maria Lopez?"
                  confirmLabel="Deactivate"
                  onConfirm={() => setDemoConfirmOpen(false)}
                  onClose={() => setDemoConfirmOpen(false)}
                >
                  <Typography variant="body2">
                    Maria Lopez will no longer be able to access Demo Agency. Their profile, roles,
                    and security attributes will be kept, and you can reactivate them at any time.
                  </Typography>
                </ConfirmDialog>
              </Box>
            </CardContent>
          </ComponentCard>

          <ComponentCard>
            <CardHeader
              title="RowActions"
              subheader="The Actions column of every admin table. Edit always comes first, then any module-specific actions, then Disable (Deactivate for people), then Delete. Pass only the actions the entity needs."
            />
            <CardContent>
              <Box
                sx={{ border: '1px dashed #ccc', p: 2, borderRadius: 1 }}
                data-testid="demo-row-actions"
              >
                <Typography variant="subtitle2">
                  Config entity — variant &quot;disable&quot;
                </Typography>
                <RowActions
                  entityLabel="Tenant"
                  testIdPrefix="demo-tenant"
                  onEdit={() => undefined}
                  secondaryActions={
                    <RowActionButton
                      label="Revert to Global Tenant"
                      icon={<RestoreIcon fontSize="small" color="warning" />}
                      onClick={() => undefined}
                    />
                  }
                  deactivation={{
                    variant: 'disable',
                    isActive: demoRowActive,
                    onToggle: () => setDemoRowActive((active) => !active),
                  }}
                  onDelete={() => undefined}
                  deleteDisabled
                  deleteDisabledReason="Cannot delete GLOBAL tenant"
                />
                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  Person — variant &quot;deactivate&quot;, Remove from tenant
                </Typography>
                <RowActions
                  entityLabel="User"
                  testIdPrefix="demo-user"
                  onEdit={() => undefined}
                  deactivation={{
                    variant: 'deactivate',
                    isActive: demoUserActive,
                    onToggle: () => setDemoUserActive((active) => !active),
                  }}
                  onDelete={() => undefined}
                  deleteVerb="Remove"
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1 }}
                >
                  {`<RowActions entityLabel="Tenant" rowLabel={row.name} testIdPrefix="tenant" onEdit={…} deactivation={{ variant: 'disable', isActive, onToggle }} onDelete={…} />`}
                </Typography>
              </Box>
            </CardContent>
          </ComponentCard>

          <ComponentCard>
            <CardHeader title="Other Custom Components" />
            <CardContent>
              <Box sx={{ border: '1px dashed #ccc', p: 2, borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Additional custom components will be added here as they are developed.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This section will showcase all our custom-built components that extend or
                  complement the MUI component library.
                </Typography>
              </Box>
            </CardContent>
          </ComponentCard>
        </TabPanel>

        <TabPanel value={tabValue} index={6}>
          <Typography variant="h6" gutterBottom>
            Metadata Form
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Interactive demonstration of the MetadataForm component with a mock "sample-model"
            containing various field types. This form showcases all field rendering capabilities
            including single select, multiselect, date, currency, text, and boolean fields.
          </Typography>

          <ComponentCard>
            <CardHeader title="MetadataForm Component" />
            <CardContent>
              {metadataFormLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : metadataFormError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {metadataFormError}
                </Alert>
              ) : mockViewConfig ? (
                <Box sx={{ border: '1px dashed #ccc', p: 2, borderRadius: 1 }}>
                  <MetadataForm
                    modelId="sample-model"
                    tenantId="GLOBAL"
                    mode="create"
                    viewConfig={mockViewConfig}
                    showSectionDivider={false}
                    sectionTitle=""
                    columnLayout="double"
                  />
                </Box>
              ) : null}
            </CardContent>
          </ComponentCard>
        </TabPanel>
      </DemoContainer>
    </SkeletonPage>
  );
};

export default ComponentLibraryDemo;
